/**
 * Rental Agreement + terms checkbox. Draws copy from public `settings`
 * so global changes (min age, mileage, fees) flow into the terms text
 * without a redeploy.
 */
export default function TermsAgreement({ b, settings, agreed, onAgreedChange }) {
  const depositLine = (b?.security_deposit || 0) > 0
    ? ` (€${b.security_deposit.toFixed(0)} security deposit applies — ${(b?.financial_summary?.security_deposit_pct || 0).toFixed(0)}% of reservation total)`
    : "";

  return (
    <div className="mt-10">
      <p className="overline mb-3">Rental Agreement</p>
      <h2 className="font-display text-2xl mb-4">Terms & Conditions</h2>
      <div
        className="bg-[#EFECE6] border border-black/5 p-6 max-h-96 overflow-y-auto text-sm leading-relaxed text-neutral-700 space-y-3"
        data-testid="rental-agreement"
      >
        <p><strong>1. Reservation.</strong> This agreement constitutes a binding rental contract between you (the "Renter") and {settings?.company_name || "Brainart Creative Technologies"} ("Operator") for the vehicle listed in this booking.</p>
        <p><strong>2. Driver Eligibility.</strong> Renter must be at least {settings?.min_driver_age ?? 21} years of age and hold a valid driver's license. License details provided in this booking will be verified at pickup.</p>
        <p><strong>3. Vehicle Use.</strong> The vehicle shall be used only by the Renter and approved additional drivers. Off-road use is prohibited unless explicitly permitted (e.g., Wrangler Rubicon).</p>
        <p><strong>4. Insurance.</strong> Basic third-party liability insurance is included. Full Insurance add-on covers collision damage waiver and theft protection up to vehicle value.</p>
        <p><strong>5. Fuel.</strong> The vehicle is delivered with a full tank and must be returned with a full tank. Refuelling fees of €{(settings?.fuel_refill_fee_per_gallon ?? 9).toFixed(2)}/gallon apply otherwise.</p>
        <p><strong>6. Mileage.</strong> Includes {settings?.included_miles_per_day ?? 200} miles per day. Excess mileage charged at €{(settings?.excess_mile_fee ?? 0.35).toFixed(2)}/mile.</p>
        <p><strong>7. Damage.</strong> Renter is responsible for any damage to the vehicle not covered by the selected insurance package, up to the deductible amount{depositLine}.</p>
        <p><strong>8. Cancellation.</strong> Free cancellation up to {settings?.cancellation_window_hours ?? 24} hours before pickup. Cancellations within that window forfeit {settings?.cancellation_fee_pct ?? 50}% of total. No-shows forfeit full amount.</p>
        <p><strong>9. Late Returns.</strong> Late returns incur a fee of {settings?.late_return_fee_per_6h_pct ?? 100}% of the daily rate per 6 hours overdue.</p>
        <p><strong>10. Governing Law.</strong> This agreement shall be governed by and construed in accordance with the laws of California, USA.</p>
        <p>By checking the box below, you confirm that you have read, understood, and agreed to these Terms & Conditions.</p>
      </div>

      <label className="flex items-start gap-3 mt-6 cursor-pointer">
        <input
          type="checkbox"
          checked={agreed}
          onChange={(e) => onAgreedChange(e.target.checked)}
          className="mt-1 w-4 h-4 accent-black"
          data-testid="agree-terms-checkbox"
        />
        <span className="text-sm">
          I have read and agree to the Operator's <strong>Rental Agreement</strong> and <strong>Terms of Service</strong>.
        </span>
      </label>
    </div>
  );
}
